import React from 'react';
import { ImageType } from './TypeFilter';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { 
  TypeSelector, 
  DescriptionField, 
  DimensionsSelector, 
  FilterTabs 
} from './components';
import { SimpleAdvancedToggle } from './components/SimpleAdvancedToggle';
import ProjectSelectionDropdown from '../ImageGeneration/ProjectSelectionDropdown';
import styles from './FilterBar.module.css';

interface FilterBarProps {
  selectedType: ImageType | null;
  onTypeChange: (type: ImageType | null) => void;
  styleOptions: FilterOption[];
  selectedStyle: string | null;
  onStyleChange: (options: FilterOption[]) => void;
  setSelectedStyle: (style: string) => void;
  influenceOptions: FilterOption[];
  selectedInfluence: string | null;
  onInfluenceChange: (options: FilterOption[]) => void;
  setSelectedInfluence: (influence: string) => void;
  colorOptions: FilterOption[];
  onColorChange: (options: FilterOption[]) => void;
  backgroundOptions: FilterOption[];
  onBackgroundChange: (options: FilterOption[]) => void;
  selectedBackground: string | null;
  environmentOptions: FilterOption[];
  onEnvironmentChange: (options: FilterOption[]) => void;
  moodOptions: FilterOption[];
  onMoodChange: (options: FilterOption[]) => void;
  imageDescription: string;
  onImageDescriptionChange: (value: string) => void;
  width: string;
  height: string;
  onWidthChange: (value: string | string[]) => void;
  onHeightChange: (value: string | string[]) => void;
  isAdvancedMode: boolean;
  setIsAdvancedMode: (value: boolean) => void;
  selectedProjectId?: string | null;
  onProjectChange?: (projectId: string | null) => void;
}

const FilterBar: React.FC<FilterBarProps> = React.memo(({
  selectedType,
  onTypeChange,
  styleOptions,
  selectedStyle,
  onStyleChange,
  setSelectedStyle,
  influenceOptions,
  selectedInfluence,
  onInfluenceChange,
  setSelectedInfluence,
  colorOptions,
  onColorChange,
  backgroundOptions,
  onBackgroundChange,
  selectedBackground,
  environmentOptions,
  onEnvironmentChange,
  moodOptions,
  onMoodChange,
  imageDescription,
  onImageDescriptionChange,
  width,
  height,
  onWidthChange,
  onHeightChange,
  isAdvancedMode,
  setIsAdvancedMode,
  selectedProjectId,
  onProjectChange
}) => {
  return (
    <div className="space-y-6">
        {/* Project Selection - At the very top */}
        {onProjectChange && (
          <div className="w-full">
            <ProjectSelectionDropdown
              selectedProjectId={selectedProjectId}
              onProjectChange={onProjectChange}
            />
          </div>
        )}
        
        {/* Image Prompt Field */}
        <div className="w-full">
          <DescriptionField 
            imageDescription={imageDescription} 
            onImageDescriptionChange={onImageDescriptionChange}
          />
        </div>
        
        <div>
          {/* Image Type Header */}
          <div className="mb-3">
            <h3 className="text-sm font-medium mb-1 text-purple-600 dark:text-purple-400">Image Type</h3>
          </div>
          
          {/* Image Type Selection */}
          <TypeSelector 
            selectedType={selectedType} 
            onTypeChange={onTypeChange} 
          />
        </div>

        {/* Only show Simple/Advanced Toggle when an image type is selected */}
        {selectedType && (
          <div className="w-full">
            <SimpleAdvancedToggle 
              isAdvanced={isAdvancedMode}
              onToggle={setIsAdvancedMode}
            />
          </div>
        )}

        {selectedType && (
          <div className={`${styles.filterCard}`}>
            <FilterTabs 
              styleOptions={styleOptions}
              selectedStyle={selectedStyle}
              onStyleChange={onStyleChange}
              setSelectedStyle={setSelectedStyle}
              influenceOptions={influenceOptions}
              selectedInfluence={selectedInfluence}
              onInfluenceChange={onInfluenceChange}
              setSelectedInfluence={setSelectedInfluence}
              colorOptions={colorOptions}
              onColorChange={onColorChange}
              backgroundOptions={backgroundOptions}
              onBackgroundChange={onBackgroundChange}
              selectedBackground={selectedBackground}
              environmentOptions={environmentOptions}
              onEnvironmentChange={onEnvironmentChange}
              moodOptions={moodOptions}
              onMoodChange={onMoodChange}
              isAdvancedMode={isAdvancedMode}
            />
          </div>
        )}
        
        {/* Dimension Controls */}
        <div className="w-full mt-6">
          <DimensionsSelector 
            width={width}
            height={height}
            onWidthChange={onWidthChange}
            onHeightChange={onHeightChange}
          />
        </div>
    </div>
  );
});

FilterBar.displayName = 'FilterBar';

export default FilterBar;
